import { Suspense, lazy, useEffect, useMemo, useState, type ComponentType } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from './components/Header';
import { CartPanel } from './components/CartPanel';
import { Footer } from './components/Footer';
import { HomePage } from './components/HomePage';
import { siteContent } from './data/siteContent';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider, useCart } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { fetchArtworks, fetchProducts } from './lib/api';
import { convertImperialToMetricLabel } from './lib/measurements';
import type { Artwork, Product } from './types';
import { ensureVisitRecorded, trackAnalyticsEvent } from './lib/analytics';
import { trackPageView } from './lib/gtag';
import { deferToLoadAndIdle } from './lib/idle';
import interLatin400Url from '@fontsource/inter/files/inter-latin-400-normal.woff2?url'
import interLatin500Url from '@fontsource/inter/files/inter-latin-500-normal.woff2?url'
import interLatin600Url from '@fontsource/inter/files/inter-latin-600-normal.woff2?url'
import interCyrillic400Url from '@fontsource/inter/files/inter-cyrillic-400-normal.woff2?url'
import interCyrillic500Url from '@fontsource/inter/files/inter-cyrillic-500-normal.woff2?url'
import interCyrillic600Url from '@fontsource/inter/files/inter-cyrillic-600-normal.woff2?url'
import playfairLatin400Url from '@fontsource/playfair-display/files/playfair-display-latin-400-normal.woff2?url'
import playfairLatin500Url from '@fontsource/playfair-display/files/playfair-display-latin-500-normal.woff2?url'
import playfairLatin600Url from '@fontsource/playfair-display/files/playfair-display-latin-600-normal.woff2?url'
import playfairCyrillic400Url from '@fontsource/playfair-display/files/playfair-display-cyrillic-400-normal.woff2?url'
import playfairCyrillic500Url from '@fontsource/playfair-display/files/playfair-display-cyrillic-500-normal.woff2?url'
import playfairCyrillic600Url from '@fontsource/playfair-display/files/playfair-display-cyrillic-600-normal.woff2?url'
import { embeddedArtworks } from './data/embeddedArtworks'

const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'))
const CommissionPage = lazyNamedImport(() => import('./components/CommissionPage'), 'CommissionPage')
const ArtworksPage = lazyNamedImport(() => import('./components/ArtworksPage'), 'ArtworksPage')
const ArtworkDetailPage = lazyNamedImport(
  () => import('./components/ArtworkDetailPage'),
  'ArtworkDetailPage',
)
const ShippingPolicyPage = lazyNamedImport(
  () => import('./components/ShippingPolicyPage'),
  'ShippingPolicyPage',
)
const ReturnPolicyPage = lazyNamedImport(
  () => import('./components/ReturnPolicyPage'),
  'ReturnPolicyPage',
)
const LegalNoticePage = lazyNamedImport(
  () => import('./components/LegalNoticePage'),
  'LegalNoticePage',
)
const PrivacyPolicyPage = lazyNamedImport(
  () => import('./components/PrivacyPolicyPage'),
  'PrivacyPolicyPage',
)
const CustomerSupportPage = lazyNamedImport(
  () => import('./components/CustomerSupportPage'),
  'CustomerSupportPage',
)
const AboutPage = lazyNamedImport(() => import('./components/AboutPage'), 'AboutPage')
const BlogPage = lazyNamedImport(() => import('./components/BlogPage'), 'BlogPage')
const BlogPostPage = lazyNamedImport(() => import('./components/BlogPostPage'), 'BlogPostPage')
const ReviewsPage = lazyNamedImport(() => import('./components/ReviewsPage'), 'ReviewsPage')
const ThankYouPage = lazyNamedImport(() => import('./components/ThankYouPage'), 'ThankYouPage')
const CheckoutPage = lazyNamedImport(() => import('./components/CheckoutPage'), 'CheckoutPage')
const AdminLogin = lazyNamedImport(() => import('./components/admin/AdminLogin'), 'AdminLogin')
const AdminOrderDetail = lazyNamedImport(
  () => import('./components/admin/AdminOrderDetail'),
  'AdminOrderDetail',
)
const ContactPage = lazyNamedImport(() => import('./components/ContactPage'), 'ContactPage')
const OrderStatusPage = lazyNamedImport(
  () => import('./components/OrderStatusPage'),
  'OrderStatusPage',
)

function GlobalFontPreload() {
  return (
    <Helmet>
      <link rel="preload" as="font" type="font/woff2" href={interLatin400Url} crossOrigin="anonymous" />
      <link rel="preload" as="font" type="font/woff2" href={interLatin500Url} crossOrigin="anonymous" />
      <link rel="preload" as="font" type="font/woff2" href={interLatin600Url} crossOrigin="anonymous" />
      <link rel="preload" as="font" type="font/woff2" href={interCyrillic400Url} crossOrigin="anonymous" />
      <link rel="preload" as="font" type="font/woff2" href={interCyrillic500Url} crossOrigin="anonymous" />
      <link rel="preload" as="font" type="font/woff2" href={interCyrillic600Url} crossOrigin="anonymous" />
      <link rel="preload" as="font" type="font/woff2" href={playfairLatin400Url} crossOrigin="anonymous" />
      <link rel="preload" as="font" type="font/woff2" href={playfairLatin500Url} crossOrigin="anonymous" />
      <link rel="preload" as="font" type="font/woff2" href={playfairLatin600Url} crossOrigin="anonymous" />
      <link rel="preload" as="font" type="font/woff2" href={playfairCyrillic400Url} crossOrigin="anonymous" />
      <link rel="preload" as="font" type="font/woff2" href={playfairCyrillic500Url} crossOrigin="anonymous" />
      <link rel="preload" as="font" type="font/woff2" href={playfairCyrillic600Url} crossOrigin="anonymous" />
    </Helmet>
  )
}

function lazyNamedImport(
  loader: () => Promise<Record<string, unknown>>,
  exportName: string,
) {
  return lazy(async () => {
    const module = await loader()
    const component = module[exportName]
    if (!component) {
      throw new Error(`Module "${exportName}" is not exported by the requested file.`)
    }
    return { default: component as ComponentType }
  })
}

const PRODUCT_IMAGE_OVERRIDES = [
  { identifier: '1763659580230-1-(1)', basePath: '/media/product-1763659580' },
  { identifier: '1763657331441-20250522_104432', basePath: '/media/product-1763657331' },
  { identifier: '1763871827648-lola', basePath: '/media/product-1763871827648' },
  { identifier: '1763870776058-hulk', basePath: '/media/product-1763870776058' },
  { identifier: '1763657095299-20251029_115312', basePath: '/media/product-1763657095299' },
  { identifier: '1763871388165-leia', basePath: '/media/product-1763871388165' },
  { identifier: '1763659437680-20241118_104552(1)', basePath: '/media/product-1763659437680' },
  { identifier: '1763657118840-20251111_141151', basePath: '/media/product-1763657118840' },
  { identifier: 'photo-1472214103451-9374bd1c798e', basePath: '/media/product-1763871388165' },
] as const

const ARTWORK_CACHE_KEY = 'artworks_cache_v1'

const fallbackArtworks: Artwork[] = embeddedArtworks

const loadCachedArtworks = (): Artwork[] => {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(ARTWORK_CACHE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const persistArtworksCache = (entries: Artwork[]) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(ARTWORK_CACHE_KEY, JSON.stringify(entries))
  } catch {
    // ignore quota errors
  }
}

const fallbackProducts: Product[] = [
  {
    id: 1,
    slug: siteContent.featuredProduct.slug,
    eyebrow: undefined,
    title: siteContent.featuredProduct.title,
    description: siteContent.featuredProduct.description,
    baseImage: siteContent.featuredProduct.baseImage,
    baseImagePosition: 'center center',
    shippingNote: siteContent.featuredProduct.shippingNote,
    sliderSpeed: 800,
    sliderAutoplaySpeed: 5000,
    sliderAutoplayEnabled: true,
    sizes: siteContent.featuredProduct.sizes.map((size, idx) => {
      const metricLabel = convertImperialToMetricLabel(size.name)
      return {
        id: idx + 1,
        productId: 1,
        label: size.name,
        labelImperial: size.name,
        labelMetric: metricLabel || size.name,
        price: size.price,
      }
    }),
  },
  {
    id: 2,
    slug: siteContent.featuredInkProduct.slug,
    eyebrow: undefined,
    title: siteContent.featuredInkProduct.title,
    description: siteContent.featuredInkProduct.description,
    baseImage: siteContent.featuredInkProduct.baseImage,
    baseImagePosition: 'center center',
    shippingNote: siteContent.featuredInkProduct.shippingNote,
    sliderSpeed: 800,
    sliderAutoplaySpeed: 5000,
    sliderAutoplayEnabled: true,
    sizes: siteContent.featuredInkProduct.sizes.map((size, idx) => {
      const metricLabel = convertImperialToMetricLabel(size.name)
      return {
        id: idx + 101,
        productId: 2,
        label: size.name,
        labelImperial: size.name,
        labelMetric: metricLabel || size.name,
        price: size.price,
      }
    }),
  },
]

function overrideProductImageSrc(src?: string | null) {
  if (!src) return ''
  const override = PRODUCT_IMAGE_OVERRIDES.find(({ identifier }) => src.includes(identifier))
  if (!override) {
    return src
  }
  const widthMatch = src.match(/_w(\d+)/i)
  const widthSuffix = widthMatch ? `_w${widthMatch[1]}` : '_w1200'
  return `${override.basePath}${widthSuffix}.webp`
}

function applyLocalProductMedia(product: Product): Product {
  return {
    ...product,
    baseImage: overrideProductImageSrc(product.baseImage) || product.baseImage,
    images: product.images?.map((src) => overrideProductImageSrc(src) || src),
  }
}

const fallbackProductsWithLocalMedia = fallbackProducts.map(applyLocalProductMedia)

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppShell() {
  const location = useLocation()
  const isCheckoutRoute = location.pathname === '/checkout'
  const isArtworksRoute = location.pathname.startsWith('/artworks')
  const cachedArtworks = useMemo(() => loadCachedArtworks(), [])
  const initialArtworks = cachedArtworks.length ? cachedArtworks : fallbackArtworks
  const [artworks, setArtworks] = useState<Artwork[]>(initialArtworks);
  const [products, setProducts] = useState<Product[]>(fallbackProductsWithLocalMedia);
  const [loading, setLoading] = useState(() => cachedArtworks.length === 0 && fallbackArtworks.length === 0);
  const [productsLoading, setProductsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true
    const runFetches = () => {
      fetchArtworks()
        .then((data) => {
          if (!active) return
          setArtworks(data)
          persistArtworksCache(data)
          setError(null)
        })
        .catch((err) => {
          if (!active) return
          setError(err.message)
        })
        .finally(() => {
          if (active) setLoading(false)
        })
      fetchProducts()
        .then((data) => {
          if (!active) return
          if (data.length) {
            setProducts(data.map(applyLocalProductMedia))
          } else {
            setProducts(fallbackProductsWithLocalMedia)
          }
        })
        .catch(() => {
          if (!active) return
          setProducts(fallbackProductsWithLocalMedia)
        })
        .finally(() => {
          if (active) setProductsLoading(false)
        })
    }
    if (isArtworksRoute) {
      runFetches()
      return () => {
        active = false
      }
    }
    const cancelIdle = deferToLoadAndIdle(runFetches, 1500, 700)
    return () => {
      active = false
      cancelIdle()
    }
  }, [isArtworksRoute])

  useEffect(() => {
    const cancelIdle = deferToLoadAndIdle(() => {
      ensureVisitRecorded().catch((error) => {
        if (import.meta.env.DEV) {
          console.warn('Visit tracking failed', error)
        }
      })
    }, 2000, 400)
    return cancelIdle
  }, [])


  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`)
  }, [location.pathname, location.search])

  const { isAuthenticated } = useAuth();
  const { totalQuantity } = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);

  const openCartPanel = () => {
    setIsCartOpen((prev) => {
      if (!prev) {
        trackAnalyticsEvent('cart_open', { metadata: { trigger: 'header_open' } })
      }
      return true
    })
  }
  const closeCartPanel = () => {
    setIsCartOpen((prev) => {
      if (prev) {
        trackAnalyticsEvent('cart_close', { metadata: { trigger: 'header_close' } })
      }
      return false
    })
  }
  const toggleCartPanel = () =>
    setIsCartOpen((prev) => {
      const next = !prev
      trackAnalyticsEvent(next ? 'cart_open' : 'cart_close', { metadata: { trigger: 'header_toggle' } })
      return next
    })

  return (
    <>
    <GlobalFontPreload />
    <div className="min-h-screen flex flex-col">
      {!isCheckoutRoute && (
        <Header
          brand={siteContent.brand}
          navigation={siteContent.navigation}
          cartCount={totalQuantity}
          onCartToggle={toggleCartPanel}
        />
      )}
      <main className="flex-1">
        {error && (
          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg">{error}</div>
          </div>
        )}
        <Suspense fallback={<div className="p-8 text-center text-sm text-foreground/60">Loading experience…</div>}>
          <Routes>
          <Route
            path="/"
            element={
              <HomePage
                artworks={artworks}
                loading={loading}
                products={products}
                productsLoading={productsLoading}
                brandCopy={siteContent.brand}
                fallbackCategories={siteContent.gallery.categories}
                onCartOpen={openCartPanel}
                cartOpen={isCartOpen}
              />
            }
          />
          <Route
            path="/artworks"
            element={
              <ArtworksPage
                artworks={artworks}
                loading={loading}
              />
            }
          />
          <Route path="/artworks/:artworkId" element={<ArtworkDetailPage />} />
          <Route
            path="/commission"
            element={
              <CommissionPage
                products={products}
                productsLoading={productsLoading}
                onCartOpen={openCartPanel}
              />
            }
          />
          <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
          <Route path="/refund-policy" element={<ReturnPolicyPage />} />
          <Route path="/legal-notice" element={<LegalNoticePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/support" element={<CustomerSupportPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:postId" element={<BlogPostPage />} />
          <Route path="/order-status/:statusKey" element={<OrderStatusPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route
            path="/admin"
            element={
              isAuthenticated ? (
                <Suspense
                  fallback={
                    <div className="p-8 text-center text-sm text-foreground/60">Loading admin dashboard…</div>
                  }
                >
                  <AdminDashboard />
                </Suspense>
              ) : (
                <Navigate to="/admin/login" replace />
              )
            }
          />
          <Route
            path="/admin/orders/:orderId"
            element={isAuthenticated ? <AdminOrderDetail /> : <Navigate to="/admin/login" replace />}
          />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="*"
            element={
              <div className="max-w-4xl mx-auto px-4 py-20 text-center">
                <h1 className="text-4xl mb-4">Page not found</h1>
                <p className="text-foreground/70">The page you’re looking for doesn’t exist.</p>
              </div>
            }
          />
          </Routes>
        </Suspense>
      </main>
      {!isCheckoutRoute && <Footer footer={siteContent.footer} />}
      {!isCheckoutRoute && <CartPanel open={isCartOpen} onClose={closeCartPanel} />}
    </div>
    </>
  );
}

export default function App() {
  useEffect(() => {
    const RETRY_FLAG = 'chunk_retry_once'
    const handler = (event: ErrorEvent | PromiseRejectionEvent) => {
      const message =
        'reason' in event && event.reason?.message
          ? String(event.reason.message)
          : 'message' in event && event.message
            ? String(event.message)
            : ''
      if (!message.includes('Failed to fetch dynamically imported module')) return
      if (sessionStorage.getItem(RETRY_FLAG) === '1') return
      sessionStorage.setItem(RETRY_FLAG, '1')
      window.location.reload()
    }
    window.addEventListener('error', handler as EventListener)
    window.addEventListener('unhandledrejection', handler as EventListener)
    return () => {
      window.removeEventListener('error', handler as EventListener)
      window.removeEventListener('unhandledrejection', handler as EventListener)
    }
  }, [])
  return (
    <LanguageProvider>
      <CurrencyProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <ScrollToTop />
              <AppShell />
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </CurrencyProvider>
    </LanguageProvider>
  );
}
